import React, { useState } from "react";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "../ui/card";
import { Button } from "../ui/button";
import {
  Play,
  Edit,
  Clock,
  Dumbbell,
  BarChart,
  CalendarX,
  Timer,
  Bug,
} from "lucide-react";
import { DbWorkout, DbExercise } from "@/types/database";
import { cn } from "@/utils/cn";
import { Badge } from "../ui/badge";
import {
  format,
  isToday,
  isYesterday,
  differenceInDays,
  differenceInWeeks,
  differenceInMonths,
} from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { useDebugMode } from "../../contexts/DebugModeContext";

interface WorkoutCardProps {
  workout: DbWorkout;
  onStart: () => void;
  onEdit: () => void;
  lastUsed?: Date | null;
  timesUsed?: number;
  highlight?: boolean;
}

export function WorkoutCard({
  workout,
  onStart,
  onEdit,
  lastUsed,
  timesUsed = 0,
  highlight = false,
}: WorkoutCardProps) {
  const { debugMode } = useDebugMode();

  // Analyze workout type based on exercises
  const analyzeWorkoutType = (workout: DbWorkout) => {
    if (!workout.exercises || workout.exercises.length === 0) {
      return {
        type: "unknown",
        iconType: "dumbbell",
        strengthCount: 0,
        cardioCount: 0,
        gradient: "from-gray-500/20 to-slate-500/20",
      };
    }

    // Count exercises by their explicit type
    let strengthCount = 0;
    let cardioCount = 0;

    workout.exercises.forEach((exercise) => {
      if (exercise.structure.type === "sets") {
        strengthCount++;
      } else if (exercise.structure.type === "timed") {
        cardioCount++;
      } else {
        throw new Error(`Unknown exercise type: ${exercise.structure.type}`);
      }
    });

    const totalExercises = workout.exercises.length;
    const strengthRatio = strengthCount / totalExercises;
    const cardioRatio = cardioCount / totalExercises;

    // Determine the dominant type
    if (strengthCount > 0 && cardioCount === 0) {
      return {
        type: "strength",
        iconType: "dumbbell",
        strengthCount,
        cardioCount,
        strengthRatio,
        cardioRatio,
        gradient: "from-red-500/20 to-orange-500/20",
      };
    } else if (cardioCount > 0 && strengthCount === 0) {
      return {
        type: "cardio",
        iconType: "timer",
        strengthCount,
        cardioCount,
        strengthRatio,
        cardioRatio,
        gradient: "from-blue-500/20 to-cyan-500/20",
      };
    } else if (strengthCount > 0 && cardioCount > 0) {
      // Mixed workout - determine which is more dominant
      if (strengthRatio > cardioRatio) {
        return {
          type: "mixed-strength",
          iconType: "dumbbell",
          strengthCount,
          cardioCount,
          strengthRatio,
          cardioRatio,
          gradient: "from-purple-500/20 to-pink-500/20",
        };
      } else {
        return {
          type: "mixed-cardio",
          iconType: "timer",
          strengthCount,
          cardioCount,
          strengthRatio,
          cardioRatio,
          gradient: "from-emerald-500/20 to-teal-500/20",
        };
      }
    }

    // Default case - no clear type
    return {
      type: "unknown",
      iconType: "dumbbell",
      strengthCount,
      cardioCount,
      strengthRatio,
      cardioRatio,
      gradient: "from-gray-500/20 to-slate-500/20",
    };
  };

  // Get workout type analysis
  const workoutAnalysis = analyzeWorkoutType(workout);

  // Get the icon based on the analysis
  const getWorkoutIcon = () => {
    switch (workoutAnalysis.iconType) {
      case "timer":
        return <Timer className="h-5 w-5" />;
      case "dumbbell":
      default:
        return <Dumbbell className="h-5 w-5" />;
    }
  };

  const workoutIcon = getWorkoutIcon();

  // Calculate total exercises
  const totalExercises = workout.exercises?.length || 0;

  // Format the last used date in a user-friendly way
  const formatLastUsed = () => {
    if (!lastUsed) return "Never used";

    if (isToday(lastUsed)) {
      return `Today`;
    }

    if (isYesterday(lastUsed)) {
      return `Yesterday`;
    }

    const daysDiff = differenceInDays(new Date(), lastUsed);

    if (daysDiff < 7) {
      return `${daysDiff} days ago`;
    }

    const weeksDiff = differenceInWeeks(new Date(), lastUsed);
    if (weeksDiff < 4) {
      return weeksDiff === 1 ? "1 week ago" : `${weeksDiff} weeks ago`;
    }

    const monthsDiff = differenceInMonths(new Date(), lastUsed);
    if (monthsDiff < 12) {
      return monthsDiff === 1 ? "1 month ago" : `${monthsDiff} months ago`;
    }

    return format(lastUsed, "MMM d, yyyy");
  };

  // Get a badge for the workout type
  const getWorkoutTypeBadge = () => {
    switch (workoutAnalysis.type) {
      case "strength":
        return (
          <Badge
            variant="outline"
            className="bg-red-500/10 text-red-700 border-red-200"
          >
            Strength
          </Badge>
        );
      case "cardio":
        return (
          <Badge
            variant="outline"
            className="bg-blue-500/10 text-blue-700 border-blue-200"
          >
            Cardio
          </Badge>
        );
      case "mixed-strength":
        return (
          <Badge
            variant="outline"
            className="bg-purple-500/10 text-purple-700 border-purple-200"
          >
            Mixed (Strength)
          </Badge>
        );
      case "mixed-cardio":
        return (
          <Badge
            variant="outline"
            className="bg-emerald-500/10 text-emerald-700 border-emerald-200"
          >
            Mixed (Cardio)
          </Badge>
        );
      default:
        return null;
    }
  };

  // Get exercise type with improved handling for explicit type property
  const getExerciseType = (exercise: DbExercise) => {
    const structureType = exercise.structure.type;
    switch (structureType) {
      case "sets":
        return "strength";
      case "timed":
        return "cardio";
      case "interval":
        return "strength";
      case "cadence":
        return "cardio";
      default:
        throw new Error(`Unknown exercise type: ${structureType}`);
    }
  };

  return (
    <Card
      className={cn(
        "overflow-hidden hover:shadow-md transition-all duration-300 border",
        highlight && "ring-2 ring-primary/50"
      )}
    >
      {/* Card Header with Gradient based on workout type */}
      <div
        className={cn("h-3 w-full bg-gradient-to-r", workoutAnalysis.gradient)}
      />

      <CardContent className="p-5">
        <div className="flex justify-between items-start">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-bold text-lg line-clamp-1">{workout.name}</h3>
              {getWorkoutTypeBadge()}

              {/* Debug Button - only visible in debug mode */}
              {debugMode && (
                <Dialog>
                  <DialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-5 w-5 p-0 ml-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Bug className="h-3.5 w-3.5 text-muted-foreground" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto">
                    <DialogHeader>
                      <DialogTitle>
                        Debug Information: {workout.name}
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <h3 className="font-semibold mb-1">Workout Analysis</h3>
                        <div className="bg-muted p-3 rounded-md">
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div>Type:</div>
                            <div>{workoutAnalysis.type}</div>

                            <div>Icon Type:</div>
                            <div>{workoutAnalysis.iconType}</div>

                            <div>Strength Count:</div>
                            <div>{workoutAnalysis.strengthCount}</div>

                            <div>Cardio Count:</div>
                            <div>{workoutAnalysis.cardioCount}</div>

                            <div>Gradient:</div>
                            <div>{workoutAnalysis.gradient}</div>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h3 className="font-semibold mb-1">Exercises</h3>
                      </div>

                      <div>
                        <h3 className="font-semibold mb-1">Raw Workout Data</h3>
                        <div className="bg-muted p-3 rounded-md text-xs overflow-auto">
                          <pre>{JSON.stringify(workout, null, 2)}</pre>
                        </div>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              )}
            </div>
            <p className="text-muted-foreground text-sm mt-1 line-clamp-2">
              {workout.description ||
                `A custom workout with ${totalExercises} exercises`}
            </p>
          </div>
          <div
            className={cn(
              "p-2 rounded-full",
              workoutAnalysis.type === "strength"
                ? "bg-red-500/10"
                : workoutAnalysis.type === "cardio"
                ? "bg-blue-500/10"
                : workoutAnalysis.type === "mixed-strength"
                ? "bg-purple-500/10"
                : workoutAnalysis.type === "mixed-cardio"
                ? "bg-emerald-500/10"
                : "bg-primary/10"
            )}
          >
            {React.cloneElement(workoutIcon, {
              className: cn(
                "h-5 w-5",
                workoutAnalysis.type === "strength"
                  ? "text-red-600"
                  : workoutAnalysis.type === "cardio"
                  ? "text-blue-600"
                  : workoutAnalysis.type === "mixed-strength"
                  ? "text-purple-600"
                  : workoutAnalysis.type === "mixed-cardio"
                  ? "text-emerald-600"
                  : "text-primary"
              ),
            })}
          </div>
        </div>

        {/* Workout Stats */}
        <div className="flex flex-wrap gap-3 mt-4">
          <div className="flex items-center text-sm text-muted-foreground">
            <BarChart className="h-4 w-4 mr-1" />
            <span>{totalExercises} exercises</span>
          </div>

          {/* Last Used Information */}
          <div className="flex items-center text-sm">
            {!lastUsed ? (
              <Badge
                variant="outline"
                className="flex items-center gap-1 font-normal"
              >
                <CalendarX className="h-3 w-3" />
                Never used
              </Badge>
            ) : (
              <div className="flex items-center text-muted-foreground">
                <Clock className="h-4 w-4 mr-1" />
                <span>Last: {formatLastUsed()}</span>
              </div>
            )}
          </div>

          {timesUsed > 0 && (
            <div className="flex items-center text-sm text-muted-foreground">
              <span>
                Used {timesUsed} {timesUsed === 1 ? "time" : "times"}
              </span>
            </div>
          )}
        </div>

        {/* Exercise Preview */}
        {workout.exercises && workout.exercises.length > 0 && (
          <div className="mt-4">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
              Exercises
            </div>
            <div className="space-y-1">
              {workout.exercises.slice(0, 3).map((exercise, index) => {
                const exerciseType = getExerciseType(exercise);
                return (
                  <div key={index} className="text-sm flex items-center">
                    <div
                      className={cn(
                        "w-1.5 h-1.5 rounded-full mr-2",
                        exerciseType === "strength"
                          ? "bg-red-500"
                          : exerciseType === "cardio"
                          ? "bg-blue-500"
                          : exerciseType === "mixed"
                          ? "bg-purple-500"
                          : "bg-gray-500"
                      )}
                    />
                    <span className="line-clamp-1">
                      {exercise.name}
                      {debugMode && (
                        <span className="text-xs text-muted-foreground ml-2">
                          ({exerciseType})
                        </span>
                      )}
                    </span>
                  </div>
                );
              })}
              {workout.exercises.length > 3 && (
                <div className="text-sm text-muted-foreground">
                  +{workout.exercises.length - 3} more
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>

      <CardFooter className="px-5 py-3 bg-muted/30 flex justify-between">
        <Button variant="outline" size="sm" onClick={onEdit}>
          <Edit className="h-4 w-4 mr-1" />
          Edit
        </Button>
        <Button size="sm" onClick={onStart}>
          <Play className="h-4 w-4 mr-1" />
          Start
        </Button>
      </CardFooter>
    </Card>
  );
}

export default WorkoutCard;
